// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { createSelector } from 'reselect'
import { difference } from 'lodash'

import { fileIdFromPlottrProFile, isDeviceFileURL } from '../helpers/file'
import { emptyFile } from '../store/newFileState'
import { fullSystemStateSelector, fullFileStateSelector } from './fullFileFirstOrder'
import { initialSystemState } from '../store/initialSystemState'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').FileURL} FileURL
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../types/system').ConflictChanges} ConflictChanges
 * @typedef {import('../types/system').LocalAndOnlineConflictChanges} LocalAndOnlineConflictChanges
 * @typedef {import('../types/system').SeriesConflict} SeriesConflict
 * @typedef {import('../types/system').SystemProject} SystemProject
 * @typedef {import('../types/system').ProjectTemplate} ProjectTemplate
 * @typedef {import('../types/system').PlotlineTemplate} PlotlineTemplate
 * @typedef {import('../types/system').PlotlineCustomTemplate} PlotlineCustomTemplate
 * @typedef {import('../types/system').FilePermission} FilePermission
 * @typedef {import('../types/system').ConflictResolution} ConflictResolution
 */

/**
 * @type {function(PlottrState): SystemProject}
 */
export const projectSelector = createSelector(
  fullSystemStateSelector,
  ({ project }) => project ?? initialSystemState.project
)
/**
 * @type {function(PlottrState): boolean}
 */
export const projectNamingModalIsVisibleSelector = createSelector(
  projectSelector,
  ({ projectNamingModalIsVisible }) => projectNamingModalIsVisible
)
/**
 * @type {function(PlottrState): (ProjectTemplate | PlotlineTemplate | PlotlineCustomTemplate | Null)}
 */
export const newProjectTemplateSelector = createSelector(
  projectSelector,
  ({ template }) => template
)
/**
 * @type {function(PlottrState): FilePermission | Null}
 */
export const filePermissionSelector = createSelector(
  projectSelector,
  ({ permission }) => permission
)
/**
 * @type {function(PlottrState): boolean}
 */
export const fileLoadedSelector = createSelector(projectSelector, ({ fileLoaded }) => fileLoaded)
/**
 * @type {function(PlottrState): boolean}
 */
export const isOfflineSelector = createSelector(projectSelector, ({ isOffline }) => isOffline)
/**
 * @type {function(PlottrState): boolean}
 */
export const isResumingSelector = createSelector(projectSelector, ({ resuming }) => resuming)
/**
 * @type {function(PlottrState): boolean}
 */
export const isCheckingForOfflineDriftSelector = createSelector(
  projectSelector,
  ({ checkingOfflineDrift }) => checkingOfflineDrift
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isOverwritingCloudWithBackupSelector = createSelector(
  projectSelector,
  ({ overwritingCloudWithBackup }) => overwritingCloudWithBackup
)
/**
 * @type {function(PlottrState): boolean}
 */
export const showResumeMessageDialogSelector = createSelector(
  projectSelector,
  ({ showResumeMessageDialog }) => showResumeMessageDialog
)
/**
 * @type {function(PlottrState): boolean}
 */
export const backingUpOfflineFileSelector = createSelector(
  projectSelector,
  ({ backingUpOfflineFile }) => backingUpOfflineFile
)
/**
 * @type {function(PlottrState): (FileURL | Null)}
 */
export const fileURLSelector = createSelector(projectSelector, ({ fileURL }) => fileURL)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const selectedFileIdSelector = createSelector(fileURLSelector, (fileURL) => {
  if (fileURL && typeof fileURL === 'string') {
    return fileIdFromPlottrProFile(fileURL)
  } else {
    return null
  }
})
/**
 * @type {function(PlottrState): boolean}
 */
export const fileURLLoadedSelector = createSelector(fileURLSelector, (fileURL) => {
  return !!(fileURL && typeof fileURL === 'string' && fileURL.length && fileURL)
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isDeviceFileSelector = createSelector(
  fileURLSelector,
  (fileURL) => !!(fileURL && isDeviceFileURL(fileURL))
)
/**
 * @type {function(PlottrState): boolean}
 */
export const hasAllKeysSelector = createSelector(fullFileStateSelector, (fileState) => {
  const emptyFileState = emptyFile('DummyFile', '2022.11.2')
  return difference(Object.keys(emptyFileState), Object.keys(fileState)).length === 0
})

/**
 * @type {function(PlottrState): boolean}
 */
export const unsavedChangesSelector = createSelector(projectSelector, ({ unsavedChanges }) => {
  return unsavedChanges
})

/**
 * @type {function(PlottrState): (ConflictResolution | Null)}
 */
const conflictResolutionChangesSelector = createSelector(
  projectSelector,
  ({ conflictResolution }) => {
    return conflictResolution ?? null
  }
)

/**
 * @type {function(PlottrState): (ConflictChanges | Null)}
 */
export const localOnlyConflictChangesSelector = createSelector(
  conflictResolutionChangesSelector,
  (conflictChanges) => {
    if (conflictChanges) {
      return conflictChanges.localOnly
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (LocalAndOnlineConflictChanges | Null)}
 */
export const localAndOnlineConflictChangesSelector = createSelector(
  conflictResolutionChangesSelector,
  (conflictChanges) => {
    if (conflictChanges) {
      return conflictChanges.changedLocalAndOnline
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (SeriesConflict | Null)}
 */
export const seriesConflictSelector = createSelector(
  conflictResolutionChangesSelector,
  (conflictChanges) => {
    if (conflictChanges) {
      return conflictChanges.seriesConflict
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (PlottrFile | Null)}
 */
export const onlineFileForConflictResolutionSelector = createSelector(
  conflictResolutionChangesSelector,
  (conflictChanges) => {
    if (conflictChanges) {
      return conflictChanges.onlineFile
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (Record<String, Array<LegacyCustomAttribute>> | Null)}
 */
export const conflictResolutionCustomAttributesSelector = createSelector(
  conflictResolutionChangesSelector,
  (conflictChanges) => {
    if (conflictChanges) {
      return conflictChanges.customAttributes
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const bootedOfflineFileWhilstOnlineSelector = createSelector(
  projectSelector,
  ({ bootedOfflineFileWhilstOnline }) => {
    return bootedOfflineFileWhilstOnline
  }
)
